import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Filter, LayoutGrid, Share2, Table2 } from 'lucide-react';
import kgDatasetService from '../../../services/kgDatasetService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import KgGraphView from '../KgGraphView';
import KgRecordDrawer from '../KgRecordDrawer';
import styles from './RecordsTab.module.scss';
import {
  GRAPH_MAX_NODES,
  ORDER_BY_OPTIONS,
  buildFiltersParam,
  describeError,
  formatCoverage,
  formatNumber,
  getIngestStatusMeta,
  hasActiveFilters,
  recordNoun,
} from '../kgUtils';

const PAGE_SIZE = 50;

/**
 * Records browser (screen 5).
 *
 * Owns ONE cohort with two renderings. The filters, dates and ordering live here
 * and are passed to both views unchanged — switching view must never change
 * which records are in scope, or the graph stops being a picture of the list
 * beside it.
 *
 * Reachable in every status including `draft`: this is the pre-activation check,
 * so it deliberately sits before the Activate button rather than behind it.
 */
const RecordsTab = ({ dataset, facets, goToTab }) => {
  const [view, setView] = useState('table');
  const [filters, setFilters] = useState({});
  const [orderBy, setOrderBy] = useState('date_desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  const [tableData, setTableData] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [focus, setFocus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openRecord, setOpenRecord] = useState(null);

  const facetList = facets?.facets || [];

  // The cohort definition, shared by both views by construction.
  const cohortParams = useMemo(() => {
    const params = { order_by: orderBy };
    const filtersParam = buildFiltersParam(filters);
    if (filtersParam) params.filters = filtersParam;
    if (dateFrom) params.date_from = dateFrom;
    // `date_to` is exclusive server-side — worth remembering when reading counts.
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [filters, orderBy, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === 'table') {
        const data = await kgDatasetService.listRecords(dataset.id, {
          ...cohortParams,
          view: 'table',
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        });
        setTableData(data);
      } else {
        const data = await kgDatasetService.listRecords(dataset.id, {
          ...cohortParams,
          view: 'graph',
          max_nodes: GRAPH_MAX_NODES,
          ...(focus ? { focus } : {}),
        });
        setGraphData(data);
      }
    } catch (e) {
      setError(describeError(e, "Couldn't load records."));
    } finally {
      setLoading(false);
    }
  }, [dataset.id, view, cohortParams, page, focus]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Redefining the cohort also resets paging and the graph's focus — both are
   * positions inside a result set that no longer exists.
   *
   * Done here rather than in a reactive effect on purpose: an effect would let
   * one fetch go out against the new filters with the OLD page still set, then
   * immediately fire a second one after the page reset — a wasted request and a
   * flash of the wrong page.
   */
  const changeCohort = (apply) => {
    apply();
    setPage(0);
    setFocus(null);
  };

  const setFacetFilter = (name, value) => {
    changeCohort(() =>
      setFilters((prev) => {
        const next = { ...prev };
        if (!value || (Array.isArray(value) && value.length === 0)) delete next[name];
        else next[name] = value;
        return next;
      })
    );
  };

  // Clicking a value node in the graph adds it to the filter, which is what makes
  // the graph a navigation tool rather than a picture.
  const addValueFilter = (facetName, value) => {
    const facet = facetList.find((f) => f.name === facetName);
    changeCohort(() =>
      setFilters((prev) => {
        const current = prev[facetName];
        if (facet?.cardinality === 'many') {
          const list = Array.isArray(current) ? current : current ? [current] : [];
          if (list.includes(value)) return prev;
          return { ...prev, [facetName]: [...list, value] };
        }
        return { ...prev, [facetName]: value };
      })
    );
  };

  const clearFilters = () => {
    changeCohort(() => {
      setFilters({});
      setDateFrom('');
      setDateTo('');
    });
  };

  const active = hasActiveFilters(filters) || dateFrom || dateTo;
  const data = view === 'table' ? tableData : graphData;
  const unresolved = data?.unresolved || [];

  if (!facets && !tableData && loading) {
    return <Typography className={sharedStyles.muted}>Loading records…</Typography>;
  }

  return (
    <div className={styles.wrap}>
      {/* ── Filter bar, generated from the facet catalog ── */}
      <div className={styles.filterBar}>
        <div className={styles.filterHead}>
          <Filter size={15} />
          <span>Filters</span>
          {active && (
            <Button size="small" variant="text" onClick={clearFilters}>
              Clear all
            </Button>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
            className={styles.viewToggle}
          >
            <ToggleButton value="table">
              <Table2 size={14} />
              &nbsp;Table
            </ToggleButton>
            <ToggleButton value="graph">
              <Share2 size={14} />
              &nbsp;Graph
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <div className={styles.filterFields}>
          {facetList.map((facet) => {
            const many = facet.cardinality === 'many';
            const options = (facet.values || []).map((v) => String(v.value));
            const raw = filters[facet.name];
            // Coerce to whatever shape this control expects. A value can arrive
            // from a graph node click before the catalog told us the cardinality,
            // and handing an array to a single-select (or a string to a
            // multi-select) makes MUI throw rather than degrade. freeSolo also
            // wants '' rather than null, or the input flips to uncontrolled.
            const value = many
              ? Array.isArray(raw)
                ? raw
                : raw
                  ? [raw]
                  : []
              : Array.isArray(raw)
                ? raw[0] ?? (facet.more_values ? '' : null)
                : raw ?? (facet.more_values ? '' : null);
            return (
              <div key={facet.name} className={styles.filterField}>
                <Autocomplete
                  multiple={many}
                  size="small"
                  options={options}
                  value={value}
                  onChange={(_, next) => setFacetFilter(facet.name, next)}
                  // `more_values: true` means the list is truncated, so a plain
                  // dropdown would be silently missing options — free text keeps
                  // an unlisted value reachable.
                  freeSolo={Boolean(facet.more_values)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={facet.label || facet.name}
                      placeholder={many ? 'any of…' : 'any'}
                    />
                  )}
                  renderTags={(vals, getTagProps) =>
                    vals.map((v, i) => (
                      <Chip size="small" label={v} {...getTagProps({ index: i })} key={v} />
                    ))
                  }
                />
                <span className={styles.filterMeta}>
                  {/* Coverage travels with every count this facet can produce. */}
                  <Tooltip
                    title={
                      Number(facet.coverage_pct) < 99.5
                        ? `Only ${formatCoverage(facet.coverage_pct)} of records say anything here — filtering on it excludes the rest, which are unknown rather than non-matching.`
                        : 'Recorded on essentially every record.'
                    }
                  >
                    <span>{formatCoverage(facet.coverage_pct)} coverage</span>
                  </Tooltip>
                  {facet.more_values && <em> · list truncated</em>}
                </span>
                {facet.note && <span className={styles.filterNote}>{facet.note}</span>}
              </div>
            );
          })}

          <div className={styles.filterField}>
            <TextField
              size="small"
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => changeCohort(() => setDateFrom(e.target.value))}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className={styles.filterField}>
            <TextField
              size="small"
              type="date"
              label="Before"
              value={dateTo}
              onChange={(e) => changeCohort(() => setDateTo(e.target.value))}
              InputLabelProps={{ shrink: true }}
              helperText="Exclusive"
            />
          </div>
          <div className={styles.filterField}>
            <TextField
              select
              size="small"
              label="Order"
              value={orderBy}
              onChange={(e) => changeCohort(() => setOrderBy(e.target.value))}
            >
              {ORDER_BY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
      </div>

      {/* Values that matched nothing are reported, never dropped silently —
          otherwise the result set just looks narrower than it is. */}
      {unresolved.length > 0 && (
        <Alert severity="warning">
          <strong>Ignored: {unresolved.join(', ')}</strong> — these values didn’t match anything
          in this dataset, so they were left out of the query. The rest of your filter still
          applied.
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {/* ── The cohort ── */}
      {view === 'table' ? (
        <div className={styles.tableWrap}>
          {loading ? (
            <div className={styles.loading}>
              <CircularProgress size={22} />
            </div>
          ) : !tableData || tableData.total === 0 ? (
            <div className={sharedStyles.emptyState}>
              <LayoutGrid size={40} className={sharedStyles.emptyIcon} />
              <Typography variant="h6">
                {active ? 'Nothing matches these filters' : 'No records yet'}
              </Typography>
              <Typography variant="body2" className={sharedStyles.muted}>
                {tableData?.message ||
                  (active
                    ? 'Try widening the filters — an unmatched value returns nothing rather than falling back to everything.'
                    : 'Load a JSON export and the records will appear here.')}
              </Typography>
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                onClick={active ? clearFilters : () => goToTab('data')}
                sx={{ mt: 2 }}
              >
                {active ? 'Clear filters' : 'Load records'}
              </Button>
            </div>
          ) : (
            <>
              <div className={styles.tableMeta}>
                {formatNumber(tableData.total)} {recordNoun(dataset, tableData.total)}
                {active ? ' match' : ''}
              </div>
              <div className={styles.tableScroll}>
                <Table size="small" className={styles.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Key</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Text</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(tableData.records || []).map((r) => {
                      const meta = getIngestStatusMeta(r.ingest_status);
                      return (
                        <TableRow
                          key={r.record_key}
                          hover
                          onClick={() => setOpenRecord(r.record_key)}
                          className={styles.row}
                          // The graph is unusable with a screen reader, so this
                          // table is the accessible route to the same cohort —
                          // which only holds if the rows are actually reachable
                          // from the keyboard.
                          role="button"
                          tabIndex={0}
                          aria-label={`Open ${r.title || r.record_key}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setOpenRecord(r.record_key);
                            }
                          }}
                        >
                          <TableCell className={styles.keyCell}>
                            <code>{r.record_key}</code>
                          </TableCell>
                          <TableCell className={styles.titleCell}>{r.title || '—'}</TableCell>
                          <TableCell className={styles.dateCell}>{r.date || '—'}</TableCell>
                          <TableCell>
                            {/* The answer to "why can't the agent find the record
                                I just pushed?" — committed, not yet embedded. */}
                            <Tooltip title={meta.help}>
                              <span className={`${styles.pill} ${styles[meta.tone]}`}>
                                {meta.label}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" className={styles.numCell}>
                            {formatNumber(r.text_chars)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {/* Server-side cursor. No client-side re-sort on top of it —
                  ordering is a total order and a local sort fights the cursor. */}
              <TablePagination
                component="div"
                count={tableData.total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            </>
          )}
        </div>
      ) : (
        <KgGraphView
          data={graphData}
          loading={loading}
          focus={focus}
          onClearFocus={() => setFocus(null)}
          onSelectRecord={(key) => setOpenRecord(key)}
          onSelectValue={addValueFilter}
        />
      )}

      <KgRecordDrawer
        dataset={dataset}
        recordKey={openRecord}
        open={Boolean(openRecord)}
        onClose={() => setOpenRecord(null)}
        onFilterByValue={(name, value) => {
          addValueFilter(name, value);
          setOpenRecord(null);
        }}
        // The graph is always a bounded neighbourhood, never the whole dataset —
        // focusing a record and walking outward is the only way to explore it.
        // Offered from the table too, so "what is this connected to?" is one
        // click from a row.
        onFocusRecord={(key) => {
          setFocus(key);
          setView('graph');
          setOpenRecord(null);
        }}
      />
    </div>
  );
};

export default RecordsTab;
